import styled from '@emotion/styled';
import { Chip, InlineAlert, NavigationBar, theme } from '@travelnest/hui';
import { Button, PresetThemes } from '@travelnest/merlin.button';
import { CommissionExplainer, DealExplainer } from '@travelnest/merlin.commission-explainer';
import { Fonts } from '@travelnest/merlin.fonts';
import { StatisticPanel, StatisticPanelValue } from '@travelnest/merlin.info-panels';
import { Spacer } from '@travelnest/merlin.spacer';
import * as React from 'react';
import { ICommission } from '../../../../data/entities/commission';
import { Channel } from '../../../../entities/contracts/constants';
import { IChannel, IContracts } from '../../../../entities/contracts/types';
import { CancellationPreferenceTypes } from '../../../../entities/policies/constants';
import { FormRow, FormSelect } from '../../../components/form';
import { HelpLink } from '../../../components/help-link/HelpLink';
import { Loader } from '../../../components/loader/Loader';
import { SaveButton } from '../../../components/save-button/SaveButton';
import { calcSaveState, getChannelsCount, toTitleCase } from './helpers';
import { MarketingListItem } from './MarketingListItem';
import { ISaveState } from './reducers';
import { IMarketingPreferences } from './types';

// This version of Preferences assumes all preferences are stored at the customer level

interface IMarketingHubProps {
	customerId: string;
	marketingPreferences: IMarketingPreferences;
	commissions: ICommission;
	channels: IChannel[];
	loading: boolean;
	saving: boolean;
	saveState: ISaveState;
	touched: boolean;
	isLegacyCommission: boolean;
	onSubmit(customerId: string, marketingPreferences: IMarketingPreferences): any;
	onChange(customerId: string, marketingPreferences: IMarketingPreferences): any;
}

export interface IMarketingHubState {
	contracts: IContracts;
	policy: CancellationPreferenceTypes;
}

const ORDERED_CANCELLATION_POLICIES = [
	CancellationPreferenceTypes.STRICT,
	CancellationPreferenceTypes.MODERATE,
	CancellationPreferenceTypes.FLEXIBLE,
];

const StyledPanelSuffix = styled.span`
	font-weight: 900;
	font-size: 1.375rem;
`;

const StyledPortalCommissionExplainerWrapper = styled.div`
	max-width: 480px;

	p {
		margin: auto;
		margin-block-start: 0;
		margin-block-end: 0;
	}

	hr {
		margin: 0;
	}

	h1, h2, h3 {
		margin: 0;
	}

	h1, h2 {
		margin-right: 20px;
	}

	h3 {
		margin-bottom: 5px;
	}

	p {
		font-weight: 500;
	}

	p.vat-explainer {
		margin-top: 10px;
		color: #666666;
		font-size: 0.8125rem;
		font-weight: 300;
		line-height: 1.125rem;
	}
`;

export class MarketingPreferencesComponent extends React.Component<IMarketingHubProps, IMarketingHubState> {

	constructor(props: IMarketingHubProps) {
		super(props);
		this.state = {} as IMarketingHubState;
	}

	public onSubmit() {
		const marketingPreferences = {
			...this.props.marketingPreferences,
			cancellationPolicy: this.state.policy || this.props.marketingPreferences.cancellationPolicy
		};
		this.props.onSubmit(this.props.customerId, marketingPreferences);
	}

	public selectAllChannels() {
		const markPrefs = this.props.marketingPreferences;
		const contracts = { ...markPrefs.contracts };
		for (const channel of this.props.channels) {
			contracts[channel.id] = true;
		}
		this.setState({ contracts });
		this.props.onChange(this.props.customerId, { ...markPrefs, contracts });
	}

	public selectChannel(channel: string, value: boolean) {
		const markPrefs = this.props.marketingPreferences;
		const contracts = { ...markPrefs.contracts };
		contracts[channel] = value;
		this.setState({ contracts });
		this.props.onChange(this.props.customerId, { ...markPrefs, contracts });
	}

	public selectCancellationPolicy(policy: CancellationPreferenceTypes) {
		this.setState({ policy });
		this.props.onChange(this.props.customerId, { ...this.props.marketingPreferences, cancellationPolicy: policy });
	}

	public render() {
		const { channels, loading, saving, saveState, touched, marketingPreferences, isLegacyCommission, commissions } = this.props;
		const welcomeDealName = 'customer_first_listing_30_days';
		const welcomeDeal = (commissions?.adjustments || []).find((a) => a.reason === welcomeDealName);

		// For legacy commisson, the term is Marketing, for new customers, the term is Fees and channels.
		const titleTerm = isLegacyCommission ? 'Marketing' : 'Fees and channels';
		const selectionTerm = isLegacyCommission ? 'Marketing channels' : 'Channel selection';
		const selectionBlurb = isLegacyCommission ? 'Select the channels you’d like to list your properties on below. Take a moment to read the terms for each channel - these are found in ‘More details’ below.' : 'You can update your channel selection at any time.';
		document.title = `${titleTerm} | Travelnest`;
		const contracts = marketingPreferences.contracts || {};

		return <>
			<NavigationBar title={titleTerm} />
			<div className="portal-wrapper">
				<div className="marketing-hub">
					{loading ? <Loader loading={true} message={'Your account preferences are loading.'} />
						: <div className="marketing-hub__content">
							<SaveButton
								buttonLabel="Save changes"
								saving={saving}
								saveState={calcSaveState(saveState)}
								disabled={!touched}
								successMessage="Your changes have been saved!"
								errorMessage="There was an issue saving your changes."
								onClick={() => this.onSubmit()} />
							{!isLegacyCommission && <>
								<Fonts />
								<h2 className="marketing-hub__content-title">Fees paid on bookings</h2>
								<StyledPortalCommissionExplainerWrapper>
									<CommissionExplainer tnFeeRate={commissions?.presented_travelnest_rate} channelFeeRate={commissions?.presented_channel_rate} />
								</StyledPortalCommissionExplainerWrapper>
								{welcomeDeal && <>
									<h2 className="marketing-hub__content-title">30 day Welcome Deal!</h2>
									<StyledPortalCommissionExplainerWrapper>
										<DealExplainer adjustmentAmount={welcomeDeal?.value} description={`We'll reduce the Travelnest Fee by 2% for your first 30 days.`} />
										<p className="vat-explainer">For UK-based customers, we'll charge VAT on the fees which will be deducted from your payout.</p>
									</StyledPortalCommissionExplainerWrapper>
								</>}
							</>}
							<h2 className="marketing-hub__content-title">Cancellation preference</h2>
							<p className="marketing-hub__content-text">
								Choose a cancellation preference for your account to set the guest cancellation terms on
								all channels. We recommend Flexible to increase bookings, however, you can change this
								here.
							</p>
							<FormRow css={{ marginBottom: 10 }}>
								<FormSelect
									defaultValue={marketingPreferences.cancellationPolicy || CancellationPreferenceTypes.FLEXIBLE}
									id="cancellation_policy"
									onChange={(e) => this.selectCancellationPolicy(e.target.value as CancellationPreferenceTypes)}>
									{ORDERED_CANCELLATION_POLICIES.map((pref, i) => {
										let displayValue = pref.split('_').map((p) => toTitleCase(p)).join(' ');
										if (displayValue === 'Flexible') {
											displayValue = 'Flexible (Recommended)';
										}
										return <option
											key={i}
											value={pref}
										>{displayValue}</option>;
									})}
								</FormSelect>
								<HelpLink to="https://travelnest.freshdesk.com/support/solutions/articles/101000365053-how-do-i-set-my-cancellation-preference-">
									How do I set my cancellation preference?
								</HelpLink>
							</FormRow >

							<div style={{ borderBottom: `1px solid ${theme.colors.LM_NU30T}` }} />

							<h2 className="marketing-hub__content-title">{selectionTerm}</h2>
							{!isLegacyCommission && <>
								<StatisticPanel
									label={'You\'re live on:'}
									value={<>
										<StatisticPanelValue>{getChannelsCount(contracts, channels)}</StatisticPanelValue>
										<StyledPanelSuffix>{' '}channels 🙌</StyledPanelSuffix>
									</>}
									subtext={'All Channel Fees are 15% plus 5% Travelnest Fee'}
									colour={'green'}
								/>
							</>
							}
							<Button
								buttonType={PresetThemes.Primary}
								onClick={() => this.selectAllChannels()}
								disabled={channels.every((channel) => contracts[channel.id])}
							>
								Turn on all channels
							</Button>
							<div className="marketing-hub__selection-info">
								<HelpLink
									to="https://travelnest.freshdesk.com/support/solutions/articles/101000365068-choosing-your-channels"
								>
									How to manage my channel selection?
								</HelpLink>
								<p className="marketing-hub__selection-info-text">
									{selectionBlurb}
								</p>
							</div>
							{
								channels.filter((channel) => channel.id !== Channel.TRAVELNEST).every((channel) => !contracts[channel.id])
									? (
										<>
											<InlineAlert
												heading="All channels have been de-selected"
												content="Your property is still active and can receive bookings on Travelnest Direct, but we will no longer advertise it elsewhere. Please contact support if you want to pause or close your account."
												variant="warning"
											/>
											<Spacer spacing="standard" />
										</>
									)
									: null
							}
							<div className="marketing-hub__channels">
								<ul className="marketing-hub__channel-list">
									{channels.map((channel, idx, arr) => {
										const selected = contracts[channel.id];
										return !!channel && (
											<React.Fragment key={idx}>
												<MarketingListItem
													bgColor={theme.colors.BB20}
													content={channel.description}
													image={channel.brandedLogo}
													title={channel.display_name}
													buttonText={channel.id === Channel.TRAVELNEST ? 'Get started' : 'More details'}
													buttonUrl={`/marketing/channel/${channel.ref}`}
													selected={selected}
													onItemSelect={() => this.selectChannel(channel.id, !selected)}
													headingBadge={channel.id === Channel.TRAVELNEST ? <Chip text="Included" icon="Checkmark" color="green" /> : null}
													readOnly={channel.id === Channel.TRAVELNEST}
												>
													{
														channel.display_name === 'Expedia' ?
															<InlineAlert
																content="Expedia no longer lists properties located in France or Spain. If your property is in these countries we cannot list it on Expedia or any Expedia affiliate sites."
																variant="warning"
															/>
															:
															null
													}
												</MarketingListItem>
												{idx !== arr.length - 1 && <Spacer spacing="standard" />}
											</React.Fragment>
										);
									})}
								</ul>
							</div>
						</div >
					}
				</div>
			</div>

		</>;
	}
}
